import {SupportedLocales} from '#lit/types/locale.js';
import {CampaignTriggersExportActions} from '#lit/types/journeys.js';

export enum LocalizedDestinations {
  Iterable = 'Iterable',
}

export const DestinationLocale = {
  [SupportedLocales.EN]: {
    mlb: {
      privacySettings: {
        disclaimer:
          "You must complete the form below for MLB to confirm if your data set is eligible for automatic acceptance into the destination platform for digital advertising purposes. If your data set is not eligible for automatic acceptance into the destination for digital advertising purposes, the submission will be rejected. If your data set is accepted, any data in the data set that is not eligible for the requested campaign, e.g., due to the end user's age, location, do not sell status, etc., will be deleted by MLB.",
        endUserOptIn:
          'Did all end users with information in the data set marked for targeting opt in to receive the applicable commercial messaging? [Y/N]',
        optInLanguageApprovedVetted:
          'Was the opt in language (1) vetted by MLB and Club legal and (2) confirmed to include a link to the MLB.com Privacy Policy? Note the following format is pre approved: I would like to receive commercial emails from MLB, [Club/Club.com] and its/their partners. www.mlb.com/privacy [Y/N]',
        infoCollectedViaMLBPrivacyPolicy:
          'Was the data collected via the MLB Privacy Policy on MLB/Club digital properties or (b) from a third party location where (1) such third party is (a) an online service provider under current and valid agreement with both MLB and Club or (b) a Sponsor or Advertiser with data sharing terms in place with MLB or Club? [Y/N]',
        checkCampaignActive:
          'Ongoing exports to MLB are mapped by Export Name. Confirm the name with MLB to ensure correct audience updates.',
      },
    },
    exportDetails: 'Export Details',
    exportDetailsSubtitle: 'Choose your export name and frequency',
    exportDuration: {
      label: 'Export Duration',
      subtitle:
        'Select how long users should remain in this paid media audience',
    },
    exportName: 'Export Name',
    yourDestination: 'your Destination',
    select: 'Select',
    selectValues: 'Select values',
    optional: 'optional',
    personalizationFields: 'Personalization Fields',
    tableName: 'Table Name',
    matchFields: 'Match Fields',
    matchFieldsRequired: 'At least one match field must be filled out',
    sortingQuery: {
      ascending: 'Ascending',
      descending: 'Descending',
    },
    duration: {
      label: 'Duration',
      removeFromAudienceAfter: 'Remove from audience after',
      days: 'days',
    },
    advanceUser: {
      label: 'Advance user to next Journey step',
    },
    dv360: {
      mobileAppID: 'Mobile App ID',
    },
    campaignId: 'Campaign ID',
    errors: {
      authenticationExpiredForExport:
        'Destination authentication expired. Please re-authenticate to re-start your export.',
      authenticationExpiredForSync:
        'Destination authentication expired. Please re-authenticate to re-start your sync.',
    },
    endDate: 'End Date',
    exportInclusionCriteria: 'Export Inclusion Criteria',
    exportForm: {
      additionalFields: {
        includeAllFieldsV2: 'Include all other fields as is (without an alias)',
        includeAllFieldsV1: 'Include all other fields',
        includeGLFieldsV2:
          'Include all GrowthLoop generated fields as is (without an alias)',
        includeGLFieldsV1: 'Include all GrowthLoop generated fields',
        glFieldsTooltip:
          'GrowthLoop generated fields are system generated fields such as audience_id, audience_name, destination_name and more. They can help to better classify your clients',
        includeAllFieldsTooltip:
          'Selecting this option will include all fields from the primary dataset that this audience is built from',
      },
      userConsentField: {
        title: 'User Consent',
        subTitle:
          "Please choose the field representing a user's consent. This field must have boolean values. " +
          'GrowthLoop will automatically filter users with a false value and not send them to the destination',
        label: 'User Consent Field (optional)',
      },
      googleUserConsentFields: {
        title: 'Google specific consents',
        subTitle:
          "If you're targeting users in the EU, Google requires that you provide these consent fields as well for this destination. " +
          'Otherwise Google will exclude them from your targeting.',
      },
      dv360AppId:
        'A string that uniquely identifies your mobile app. Required for uploading audiences with Device IDs.' +
        "For iOS, this is the 9-digit ID found at the end of the App Store URL (e.g., '476943146' for 'Flood-It! 2'). " +
        "For Android, it’s the application's package name (e.g., 'com.labpixies.colordrips' for 'Color Drips')",
      qualtrics: {
        listType: {
          title: 'Add Contacts to',
          label: 'Add Contacts to',
          tooltip:
            'Add your contacts to a Mailing List to send targeted messages effectively.',
        },
        xmDirectory: {
          title: '',
          label: 'Directory ID (required)',
          tooltip:
            'The directory where contacts will be added. The specified value comes from the destination configuration, but you can override it if needed.',
        },
        mailingListType: {
          title: '',
          label: 'Mailing List Status',
          tooltip:
            'Choose between adding your contacts to a newly created Mailing List (automatically generated with the audience name) or an existing Mailing List.',
        },
        mailingList: {
          title: 'Enter Mailing List ID',
          label: 'Mailing List Id (Required)',
          tooltip:
            'Specify a Mailing List ID to add your contacts. You can find it in the Lists tab of the XM Directory by opening a list and checking the end of the URL (e.g., CG_012345678901234).',
        },
      },
      marketo: {
        audienceType: {
          title: 'Select Audience Type',
          label: 'Audience Type (Required)',
          tooltip: 'Select the type of audience you want to create.',
        },
        customObjects: {
          title: 'Select Custom Objects',
          label: 'Custom Objects',
          empty:
            "Your Marketo account currently doesn't have any Custom Objects.",
        },
      },
      marketingCloud: {
        businessUnit: {
          title: 'Select Business Unit',
          label: 'Business Unit (optional)',
          tooltip:
            'Please select the Business Unit to upload your audience. This helps segment and manage your data within the organization. If no selection is made, the Data Extension will be created in the primary Business Unit',
          empty: "Your SFMC account currently doesn't have any Business Units.",
        },
        dataFolder: {
          title: 'Select Folder',
          label: 'Data Extension Folder (optional)',
          tooltip:
            'Please select the Data Extension Folder to store your audience. This helps keep your Data Extensions organized and easy to access. If no selection is made, the Data Extension will be created in the root folder',
          empty:
            "Your SFMC account currently doesn't have any Data Extension Folders.",
        },
        dataExtension: {
          title: 'Select Data Extension',
          label: 'Data Extension',
          tooltip: 'Select the Data Extension you want to send records to',
          empty:
            "Your SFMC account currently doesn't have any Data Extensions.",
        },
      },
      redditAds: {
        audienceStatus: 'Custom Audience Status',
        audienceId: {
          title: 'Select Custom Audience',
          label: 'Custom Audience',
          tooltip: 'Select a custom audience where you want to add users.',
          empty:
            "Your Reddit Ads account currently doesn't have any Custom Audiences.",
        },
      },
      facebookAds: {
        audienceStatus: 'Custom Audience Status',
        audienceId: {
          title: 'Select Existing Audience',
          empty:
            "Your ads account currently doesn't have any existing audiences.",
        },
      },
      googleAdwords: {
        audienceStatus: 'User List Status',
        audienceId: {
          title: 'Select Existing Audience',
          empty:
            "Your ads account currently doesn't have any existing user lists.",
        },
      },
      adobe_experience_platform: {
        namespace: {
          title: 'Namespace',
          label: 'Custom Namespace ID (Optional)',
          tooltip:
            'Find your custom namespace IDs in Adobe Experience Platform > Identities and enter the corresponding Identity Symbol. This is only required if "Unique ID" is selected as the match field. To use a different namespace, create a new destination.',
        },
      },
      gam360: {
        audienceSegmentType: {
          label: 'Audience Segment Status',
          tooltip:
            'Choose whether to add user records to a newly created Audience Segment (automatically generated using the audience name) or to an existing Audience Segment.',
        },
        audienceSegment: {
          title: 'Enter Audience Segment ID',
          label: 'Audience Segment ID (Required)',
          tooltip:
            'Enter the ID of the Audience Segment you want to append user records to. You can find the Audience Segment ID in your Google Ad Manager 360 account under Inventory > Audience > Audience Segments.',
        },
      },
      sailthru: {
        apiType: {
          usersLabel: 'Users API',
          jobsLabel: 'Jobs API',
          label: 'API to Use',
          tooltip:
            'Choose the Users API for real-time updates (max 300 users per batch). Use the Jobs API for larger batch uploads (up to 5,000 users). Note: Additional Fields are only supported with the Jobs API.',
        },
      },
      liveRampActivation: {
        multiSelect: {
          label: 'Activated Destinations',
          placeholder: 'Select Activated Destination',
          noOptions: 'No destinations available.',
          tooltip:
            'Select an activated destination that has been connected and authorized in the LiveRamp account.',
        },
      },
      conversica: {
        rep_name: {
          label: 'Representative Name',
          placeholder: 'Enter Representative Name',
        },
        contact_list_name: {
          label: 'Contact List Name',
          placeholder: 'Enter Contact List Name',
        },
      },
    },
    campaignTriggers: {
      exportAction: {
        label: 'Export Action',
      },
      fromEmail: {
        label: 'From Email',
        hint: 'Choose a verified email to send your campaign from. Verified senders are approved in order to ensure compliance and safeguard your reputation.',
      },
      emailRecipient: {
        label: 'Select Field to use as Email Recipient',
        hint: "Select the field in your dataset that has the users' email addresses.",
      },
      personalizationTemplateFields: 'Personalization Template Fields',
      emailTemplate: 'Email Template',
      refreshFieldsFrom: 'Refresh fields from',
      selectEmailTemplate: 'Select Email Template...',
      datasetFields: {
        label: 'Your Dataset Fields',
        hint: 'Match the fields from your primary dataset to the dynamic fields in your email template. This ensures the right data is used for personalization.',
      },
      templateFields: {
        label: 'Template Fields',
        hint: 'Template Fields are pulled from the email template you selected. Map your dataset fields to them to personalize your emails.',
      },
      noVariables: [
        'There are no template variables for the selected template.',
        "You're all set!",
      ],
      preview: {
        thumbnailAlt: 'a thumbnail preview of the campaign template',
        fullSizeAlt: 'a full-size preview of the campaign template',
      },
      [CampaignTriggersExportActions.SEND_EMAIL]: {
        label: 'Send Test Email',
        labelTooltip: null,
        inputPlaceholder: 'hi@example.com',
        sendButton: 'Send',
        success: 'Successfully sent test email to',
        failed:
          "Sorry, we couldn't send the test email. Please ensure that you've entered a valid email address and try again.",
      },
      [CampaignTriggersExportActions.SEND_SMS]: {
        label: 'Send Test SMS',
        labelTooltip:
          'If you omit the country code, then we will automatically assume the country code is "+1" (United States & Canada). Standard carrier charges may apply',
        inputPlaceholder: '+1-212-555-1312',
        sendButton: 'Send',
        success: 'Successfully sent test SMS to',
        failed:
          "Sorry, we couldn't send the test SMS at this time. Please ensure that you've entered a valid phone number and try again.",
      },
      // below are the destination-specific locale strings
      [LocalizedDestinations.Iterable]: {
        emailTemplate: 'Campaign',
        exportAction: {
          hint: 'Choose to create a new list or send records an email',
        },
        emailRecipient: {
          label: 'Map Contact fields to Send SMS',
          hint: "When you map these fields, we'll check if a profile exists for the email. If it does, we'll send the SMS to the phone number you mapped. If not, we'll create a new profile using the email and phone number before sending the SMS to the mapped phone number.",
        },
      },
    },
  },
  [SupportedLocales.ES]: {
    mlb: {
      privacySettings: {
        disclaimer:
          'Debe completar el formulario a continuación para que MLB confirme si su conjunto de datos es elegible para la aceptación automática en la plataforma de destino con fines de publicidad digital. Si su conjunto de datos no es elegible para la aceptación automática en la plataforma de destino con fines de publicidad digital, la presentación será rechazada. Si su conjunto de datos es aceptado, cualquier dato en el conjunto de datos que no sea elegible para la campaña solicitada, por ejemplo, debido a la edad del usuario final, ubicación, estado de no vender, etc., será eliminado por MLB.',
        endUserOptIn:
          '¿Todos los usuarios finales con información en el conjunto de datos marcado para la segmentación optaron por recibir los mensajes comerciales aplicables? [S/N]',
        optInLanguageApprovedVetted:
          '¿El lenguaje de aceptación fue (1) revisado por MLB y el departamento legal del Club y (2) confirmado para incluir un enlace a la Política de Privacidad de MLB.com? Tenga en cuenta que el siguiente formato está preaprobado: Me gustaría recibir correos electrónicos comerciales de MLB, [Club/Club.com] y sus socios. www.mlb.com/privacy [S/N]',
        infoCollectedViaMLBPrivacyPolicy:
          '¿Los datos fueron recolectados a través de la Política de Privacidad de MLB en propiedades digitales de MLB/Club o (b) de una ubicación de terceros donde (1) dicho tercero es (a) un proveedor de servicios en línea bajo un acuerdo vigente y válido con MLB y el Club o (b) un Patrocinador o Anunciante con términos de intercambio de datos vigentes con MLB o el Club? [S/N]',
        checkCampaignActive:
          'Las exportaciones continuas a MLB están mapeadas por el nombre de la exportación. Confirme el nombre con MLB para garantizar actualizaciones correctas de la audiencia.',
      },
    },
    exportDetails: 'Detalles de exportación',
    exportDetailsSubtitle: 'Elija el nombre y la frecuencia de la exportación',
    exportDuration: {
      label: 'Duración de la exportación',
      subtitle:
        'Seleccione cuánto tiempo deben permanecer los usuarios en esta audiencia de medios pagos',
    },
    exportName: 'Nombre de la exportación',
    yourDestination: 'su destino',
    select: 'Seleccionar',
    selectValues: 'Seleccionar valores',
    optional: 'opcional',
    personalizationFields: 'Campos de personalización',
    tableName: 'Nombre de la tabla',
    matchFields: 'Campos de coincidencia',
    matchFieldsRequired: 'Se debe completar al menos un campo de coincidencia',
    duration: {
      label: 'Duración',
      removeFromAudienceAfter: 'Eliminar de la audiencia después de',
      days: 'días',
    },
    sortingQuery: {
      ascending: 'Ascendente',
      descending: 'Descendente',
    },
    advanceUser: {
      label: 'Avanzar al usuario al siguiente paso del recorrido',
    },
    dv360: {
      mobileAppID: 'ID de la aplicación móvil',
    },
    campaignId: 'ID de Campaña',
    errors: {
      authenticationExpiredForExport:
        'La autenticación del destino ha expirado. Por favor, vuelva a autenticarse para reiniciar su exportación.',
      authenticationExpiredForSync:
        'La autenticación del destino ha expirado. Por favor, vuelva a autenticarse para reiniciar su sincronización.',
    },
    endDate: 'Fecha de Finalización',
    exportInclusionCriteria: 'Exportar Criterios de Inclusión',
    exportForm: {
      additionalFields: {
        includeAllFieldsV2:
          'Incluir todos los demás campos tal cual (sin un alias)',
        includeAllFieldsV1: 'Incluir todos los demás campos',
        includeGLFieldsV2:
          'Incluir todos los campos generados por GrowthLoop tal cual (sin un alias)',
        includeGLFieldsV1: 'Incluir todos los campos generados por GrowthLoop',
        glFieldsTooltip:
          'Los campos generados por GrowthLoop son campos generados por el sistema, como audience_id, audience_name, destination_name y más. Pueden ayudar a clasificar mejor a tus clientes.',
        includeAllFieldsTooltip:
          'Seleccionar esta opción incluirá todos los campos del conjunto de datos principal del que se construye esta audiencia.',
      },
      userConsentField: {
        title: 'Consentimiento del Usuario (opcional)',
        subTitle:
          'Por favor, elija el campo que representa el consentimiento de un usuario. Este campo debe tener valores booleanos. ' +
          'GrowthLoop filtrará automáticamente a los usuarios con un valor falso y no los enviará al destino',
        label: 'Campo de Consentimiento del Usuario',
      },
      googleUserConsentFields: {
        title: 'Consentimientos específicos de Google',
        subTitle:
          'Si está dirigiendo a usuarios en la UE, Google requiere que proporcione estos campos de consentimiento también para este destino. ' +
          'De lo contrario, Google los excluirá de su segmentación.',
      },
      dv360AppId:
        'Una cadena que identifica de forma única su aplicación móvil. Requerido para cargar audiencias con ID de dispositivo.' +
        "Para iOS, es el ID de 9 dígitos que se encuentra al final de la URL de la App Store (por ejemplo, '476943146' para 'Flood-It! 2'). " +
        "Para Android, es el nombre del paquete de la aplicación (por ejemplo, 'com.labpixies.colordrips' para 'Color Drips')",
      qualtrics: {
        listType: {
          title: 'Agregar Contactos a',
          label: 'Agregar Contactos a',
          tooltip:
            'Agrega tus contactos a una Lista de Correo para enviar mensajes dirigidos de manera efectiva.',
        },
        xmDirectory: {
          label: 'ID de Directorio (requerido)',
          tooltip:
            'El directorio donde se agregarán los contactos. El valor especificado proviene de la configuración del destino, pero puedes anularlo si es necesario.',
        },
        mailingListType: {
          label: 'Estado de la Lista de Correo',
          tooltip:
            'Elige entre agregar tus contactos a una Lista de Correo recién creada (generada automáticamente con el nombre de la audiencia) o a una Lista de Correo existente.',
        },
        mailingList: {
          title: 'Ingresa el ID de la Lista',
          label: 'ID de la Lista de Correo (Requerido)',
          tooltip:
            'Especifica un ID de Lista de Correo para agregar tus contactos. Puedes encontrarlo en la pestaña Listas del Directorio XM abriendo una lista y revisando el final de la URL (por ejemplo, CG_012345678901234).',
        },
      },
      marketo: {
        audienceType: {
          title: 'Selecciona el Tipo de Audiencia',
          label: 'Tipo de Audiencia (Requerido)',
          tooltip: 'Selecciona el tipo de audiencia que deseas crear.',
        },
        customObjects: {
          title: 'Seleccionar Objetos Personalizados',
          label: 'Objetos Personalizados',
          empty:
            'Tu cuenta de Marketo actualmente no tiene ningún Objeto Personalizado.',
        },
      },
      marketingCloud: {
        businessUnit: {
          title: 'Seleccionar Unidad de Negocio',
          label: 'Unidad de Negocio (opcional)',
          tooltip:
            'Seleccione la Unidad de Negocio para cargar su audiencia. Esto ayuda a segmentar y administrar sus datos dentro de la organización. Si no se realiza ninguna selección, la Extensión de Datos se creará en la Unidad de Negocio principal',
          empty:
            'Su cuenta de SFMC actualmente no tiene ninguna Unidad de Negocio.',
        },
        dataFolder: {
          title: 'Seleccionar Carpeta',
          label: 'Carpeta de Datos (opcional)',
          tooltip:
            'Seleccione la Carpeta de Datos para almacenar su audiencia. Esto ayuda a mantener organizadas y fáciles de acceder sus Extensiones de Datos. Si no se realiza ninguna selección, la Extensión de Datos se creará en la carpeta raíz',
          empty:
            'Su cuenta de SFMC actualmente no tiene ninguna Carpeta de Datos.',
        },
        dataExtension: {
          title: 'Seleccionar Extensión de Datos',
          label: 'Extensión de Datos',
          tooltip:
            'Seleccione la Extensión de Datos a la que desea enviar registros',
          empty:
            'Su cuenta de SFMC actualmente no tiene ninguna Extensión de Datos.',
        },
      },
      redditAds: {
        audienceStatus: 'Estado de la Audiencia Personalizada',
        audienceId: {
          title: 'Seleccionar Audiencia Personalizada',
          label: 'Audiencia Personalizada',
          tooltip:
            'Selecciona una audiencia personalizada donde quieres agregar usuarios.',
          empty:
            'Tu cuenta de Reddit Ads actualmente no tiene ninguna Audiencia Personalizada.',
        },
      },
      facebookAds: {
        audienceStatus: 'Estado de la Audiencia Personalizada',
        audienceId: {
          title: 'Seleccionar Audiencia Existente',
          empty:
            'Tu cuenta de anuncios actualmente no tiene ninguna audiencia existente.',
        },
      },
      googleAdwords: {
        audienceStatus: 'Estado de la Lista de Usuarios',
        audienceId: {
          title: 'Seleccionar Audiencia Existente',
          empty:
            'Tu cuenta de anuncios actualmente no tiene ninguna lista de usuarios existente.',
        },
      },
      adobe_experience_platform: {
        namespace: {
          title: 'Namespace',
          label: 'ID de Espacio de Nombre (Opcional)',
          tooltip:
            'Encuentra tus IDs de espacio de nombres personalizados en Adobe Experience Platform > Identities e ingresa el Símbolo de Identidad correspondiente. Esto solo es necesario si seleccionas "Unique ID" como el campo de coincidencia. Para usar otro espacio de nombre, crea un nuevo destino.',
        },
      },
      gam360: {
        audienceSegmentType: {
          label: 'Estado del Segmento de Audiencia',
          tooltip:
            'Elige si deseas agregar registros de usuario a un Segmento de Audiencia recién creado (generado automáticamente usando el nombre de la audiencia) o a un Segmento de Audiencia existente.',
        },
        audienceSegment: {
          title: 'Ingrese el ID del Segmento de Audiencia',
          label: 'ID del Segmento de Audiencia (requerido)',
          tooltip:
            'Ingresa el ID del Segmento de Audiencia al que deseas agregar registros de usuario. Puedes encontrar el ID del Segmento de Audiencia en tu cuenta de Google Ad Manager 360 en Inventory > Audience > Audience Segments.',
        },
      },
      sailthru: {
        apiType: {
          usersLabel: 'API de Usuarios',
          jobsLabel: 'API de Trabajos',
          label: 'API a Usar',
          tooltip:
            'Elija la API de Usuarios para actualizaciones en tiempo real (máximo 300 usuarios por lote). Use la API de Trabajos para cargas de lote más grandes (hasta 5,000 usuarios). Nota: Los campos adicionales solo son compatibles con la API de Trabajos.',
        },
      },
      liveRampActivation: {
        multiSelect: {
          label: 'Destinos Activados',
          placeholder: 'Seleccionar Destino Activado',
          noOptions: 'No hay destinos disponibles.',
          tooltip:
            'Seleccione un destino activado que haya sido conectado y autorizado en la cuenta de LiveRamp.',
        },
      },
      conversica: {
        rep_name: {
          label: 'Nombre del Representante',
          placeholder: 'Ingrese el Nombre del Representante',
        },
        contact_list_name: {
          label: 'Nombre de la Lista de Contactos',
          placeholder: 'Ingrese el Nombre de la Lista de Contactos',
        },
      },
    },
    campaignTriggers: {
      exportAction: {
        label: 'Acción de exportación',
      },
      fromEmail: {
        label: 'De correo electrónico',
        hint: 'Elija un correo electrónico verificado desde el cual enviar su campaña. Los remitentes verificados se aprueban para garantizar el cumplimiento y proteger su reputación.',
      },
      emailRecipient: {
        label:
          'Seleccione el campo que se utilizará como destinatario de correo electrónico',
        hint: 'Seleccione el campo en su conjunto de datos que contiene las direcciones de correo electrónico de los usuarios.',
      },
      personalizationTemplateFields: 'Campos de plantilla de personalización',
      emailTemplate: 'Plantilla de correo electrónico',
      refreshFieldsFrom: 'Actualizar campos de',
      selectEmailTemplate: 'Seleccione la plantilla de correo electrónico...',
      datasetFields: {
        label: 'Sus campos del conjunto de datos',
        hint: 'Combine los campos de su conjunto de datos principal con los campos dinámicos de su plantilla de correo electrónico. Esto garantiza que se utilicen los datos correctos para la personalización.',
      },
      templateFields: {
        label: 'Campos de plantilla',
        hint: 'Los campos de plantilla se extraen de la plantilla de correo electrónico que seleccionaste. Asigna los campos de tu conjunto de datos a ellos para personalizar tus correos electrónicos',
      },
      noVariables: [
        'No hay variables de plantilla para la plantilla seleccionada.',
        '¡Está todo listo!',
      ],
      preview: {
        thumbnailAlt: 'vista previa en miniatura de la plantilla de campaña',
        fullSizeAlt:
          'vista previa a tamaño completo de la plantilla de campaña',
      },
      [CampaignTriggersExportActions.SEND_EMAIL]: {
        label: 'Enviar correo electrónico de prueba',
        labelTooltip: null,
        inputPlaceholder: 'hi@example.com',
        sendButton: 'Enviar',
        success: 'Correo electrónico de prueba enviado con éxito a',
        failed:
          'Lo sentimos, no pudimos enviar el correo electrónico de prueba. Asegúrese de haber ingresado una dirección de correo electrónico válida y vuelva a intentarlo',
      },
      [CampaignTriggersExportActions.SEND_SMS]: {
        label: 'Enviar mensaje de texto de prueba',
        labelTooltip:
          'Si omite el código de país, asumiremos automáticamente que el código de país es "+1" (Estados Unidos y Canadá). Se pueden aplicar cargos estándar del operador',
        inputPlaceholder: '+1-212-555-1312',
        sendButton: 'Enviar',
        success: 'Mensaje de texto de prueba enviado con éxito a',
        failed:
          'Lo sentimos, no pudimos enviar el mensaje de texto de prueba. Asegúrese de haber ingresado una dirección de número de teléfono válida y vuelva a intentarlo',
      },
      // below are the destination-specific locale strings
      [LocalizedDestinations.Iterable]: {
        emailTemplate: 'Campaña',
        exportAction: {
          hint: 'Elige crear una nueva lista o enviar un correo electrónico a los registros',
        },
        emailRecipient: {
          label: 'Asignar campos de contacto para enviar SMS',
          hint: 'Al asignar estos campos, comprobaremos si existe un perfil para el correo electrónico. Si existe, enviaremos el SMS al número de teléfono que haya asignado. De lo contrario, crearemos un nuevo perfil con el correo electrónico y el número de teléfono antes de enviar el SMS al número asignado.',
        },
      },
    },
  },
};
